import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ServiceLayout from '../../components/ServiceLayout';
import { ScrollAnimation } from '../../components/ScrollAnimation';
import { 
  SparklesIcon,
  CpuChipIcon,
  ChartBarIcon,
  BeakerIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  DocumentTextIcon,
  ChatBubbleBottomCenterTextIcon,
  CodeBracketIcon,
  CogIcon
} from '@heroicons/react/24/outline';

const TransformationShowcase = styled.section`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(11,152,206,0.1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  margin: var(--spacing-4xl) 0;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 24px;
  }
`;

const AIServices: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <CpuChipIcon />,
      title: 'Production-Ready AI Implementation',
      description: 'Comprehensive AI roadmaps tailored to your business objectives and industry requirements.',
      benefits: [
        'Custom AI strategy development',
        'Technology stack assessment',
        'ROI analysis and forecasting',
        'Implementation roadmap',
        'Change management support'
      ]
    },
    {
      icon: <ChatBubbleBottomCenterTextIcon />,
      title: 'Intelligent Automation',
      description: 'Automate complex business processes with AI-powered solutions that learn and adapt.',
      benefits: [
        'Process automation design',
        'Machine learning integration',
        'Natural language processing',
        'Predictive analytics',
        'Workflow optimization'
      ]
    },
    {
      icon: <ChartBarIcon />,
      title: 'Data Analytics & Insights',
      description: 'Transform your data into actionable insights with advanced analytics and AI modeling.',
      benefits: [
        'Advanced data modeling',
        'Real-time analytics dashboards',
        'Predictive forecasting',
        'Business intelligence integration',
        'Custom reporting solutions'
      ]
    },
    {
      icon: <BeakerIcon />,
      title: 'AI Proof of Concepts',
      description: 'Validate AI solutions with rapid prototyping and proof-of-concept development.',
      benefits: [
        'Rapid prototype development',
        'Feasibility assessment',
        'Technology validation',
        'Risk mitigation strategies',
        'Stakeholder demonstration'
      ]
    },
    {
      icon: <RocketLaunchIcon />,
      title: 'AI Integration & Deployment',
      description: 'Seamlessly integrate AI solutions into your existing systems and workflows.',
      benefits: [
        'System integration planning',
        'API development',
        'Cloud deployment strategies',
        'Performance optimization',
        'Security implementation'
      ]
    },
    {
      icon: <CogIcon />,
      title: 'AI Maintenance & Support',
      description: 'Ongoing support and optimization to ensure your AI systems continue to deliver value.',
      benefits: [
        'Model monitoring and tuning',
        'Performance optimization',
        'System updates and maintenance',
        'User training and support',
        'Continuous improvement'
      ]
    }
  ];

  const process = {
    title: 'Our AI Implementation Process',
    description: 'A proven methodology that ensures successful AI adoption and maximum business value.',
    steps: [
      {
        title: 'Discovery & Assessment',
        description: 'We analyze your current systems, data quality, and business processes to identify the best AI opportunities for your organization.'
      },
      {
        title: 'Strategy Development',
        description: 'Create a comprehensive AI strategy aligned with your business goals, including technology selection, timeline, and success metrics.'
      },
      {
        title: 'Proof of Concept',
        description: 'Develop and test AI solutions with a focused pilot project to validate approach and demonstrate value before full implementation.'
      },
      {
        title: 'Implementation & Integration',
        description: 'Deploy the AI solution into your production environment with careful attention to security, scalability, and user experience.'
      },
      {
        title: 'Training & Optimization',
        description: 'Provide comprehensive training to your team and continuously optimize the AI systems for maximum performance and ROI.'
      }
    ]
  };

  const stats = [
    { value: 'Up to 650%', label: 'Workflow Efficiency Gained' },
    { value: '>25%', label: 'Less Input Errors' },
    { value: '7+', label: 'Industries Served' }
  ];

  const cta = {
    title: 'Stop Watching Competitors Win With AI While You Wait',
    description: 'AI adoption isn\'t coming - it\'s here. We\'ll make sure you lead, not follow. Get an honest assessment of where AI can actually move your revenue needle.',
    buttonText: 'Get Your AI Readiness Assessment',
    onAction: () => navigate('/assessment')
  };

  return (
    <ServiceLayout
      serviceName="AI Solutions"
      icon={<SparklesIcon />}
      title="From Proof-of-Concept to Production Reality"
      subtitle="We're not AI researchers or theorists. We're implementers who take AI from the whiteboard to your workflow. Real integration, real data, real production environments. If it doesn't work at 3am on a Sunday with nobody watching, we haven't done our job."
      heroImage="/images/services/ai-services-hero.webp"
      stats={stats}
      features={features}
      process={process}
      cta={cta}
    />
  );
};

export default AIServices;